/**
 * AppShell
 *
 * Глобальная оболочка, оборачивающая всё приложение:
 *  - SandboxBanner — баннер sandbox-режима, если включён.
 *  - SyncBar (1.21.0) — баннер статуса синхронизации, видимый когда
 *    статус ≠ synced. При тапе ведёт в /<role>/sync. Роль определяется
 *    по pathname, чтобы один и тот же баннер работал во всех контурах.
 *    До 1.21.0 SyncBar монтировался inline на DayList/Success у техника;
 *    глобальный монтаж снимает дубликаты и даёт визибилити статуса
 *    везде.
 *  - SyncToast — auto-dismissing success-плашка.
 *
 * Также проставляет класс `app-shell--disp` для маршрутов /dispatch/*
 * (это нужно GlobalCSS чтобы снять max-width:480px ограничение только
 * для диспа).
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SyncToast }     from '../design-system/atoms/SyncToast';
import { SyncBar }       from '../design-system/atoms/SyncBar';
import { SandboxBanner } from '../design-system/atoms/SandboxBanner';
import { useSyncStore }  from '../domains/sync/syncStore';
import { useAdminBreakpoint } from '../features/admin/components/AdminDesktopShell';

/**
 * Определяет, в каком контуре сейчас находится пользователь, и возвращает
 * корректный путь к экрану очереди синхронизации. Используется в
 * глобальном SyncBar — один экран /(tech|dispatch|admin|superadmin)/sync
 * под каждой ролью, общий компонент.
 */
function resolveSyncQueuePath(pathname: string): string {
  if (pathname.startsWith('/admin'))    return '/admin/sync';
  if (pathname.startsWith('/dispatch')) return '/dispatch/sync';
  return '/tech/sync';
}

/**
 * Маршруты, на которых баннер SyncBar НЕ показываем, даже если статус
 * не synced: auth/login (там пользователь ещё не идентифицирован и
 * sync-контекст не имеет смысла), сам экран /sync (баннер на нём
 * избыточен — мы уже в очереди).
 */
function shouldHideSyncBar(pathname: string): boolean {
  if (pathname === '/' || pathname.startsWith('/auth') || pathname.startsWith('/login')) return true;
  if (pathname.endsWith('/sync')) return true;
  // /superadmin/* не имеет своего /sync — баннер скрываем, чтобы не вести в 404.
  if (pathname.startsWith('/superadmin')) return true;
  return false;
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isDisp   = location.pathname.startsWith('/dispatch');
  // 1.35.0 — Если админ-маршрут и активен десктопный лэйаут (брейкпойнт
  // ≠ mobile), снимаем мобильный max-width 480px с app-shell, чтобы
  // AdminDesktopShell растянулся на всю ширину окна. На mobile (<768px
  // или без override) max-width остаётся, мобильная вёрстка живёт как
  // раньше.
  const adminBp = useAdminBreakpoint();
  const isAdminDesktop = location.pathname.startsWith('/admin') && adminBp !== 'mobile';

  const syncStatus      = useSyncStore((s) => s.status);
  const pendingCount    = useSyncStore((s) => s.pendingCount);
  const deadLetterCount = useSyncStore((s) => s.deadLetter.length);

  const hideSyncBar = shouldHideSyncBar(location.pathname);

  const shellClass = [
    'app-shell',
    isDisp           && 'app-shell--disp',
    isAdminDesktop   && 'app-shell--admin-desktop',
  ].filter(Boolean).join(' ');

  return (
    <div className={shellClass}>
      <SandboxBanner />
      {!hideSyncBar ? (
        <SyncBar
          sync={syncStatus}
          pendingCount={pendingCount}
          deadLetterCount={deadLetterCount}
          onOpenQueue={() => navigate(resolveSyncQueuePath(location.pathname))}
        />
      ) : null}
      {children}
      <SyncToast />
      <style>{`
        @keyframes toastIn { from { opacity:0; transform:translateX(-50%) translateY(12px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes toastSlideDown { from { opacity:0; transform:translateX(-50%) translateY(-10px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
      `}</style>
    </div>
  );
}
